/**
 * The entry point of `/v1/p.js` — everything above this file, started once,
 * from a tag on somebody else's page.
 *
 * ## The whole file is inside a try/catch, and that is the point
 *
 * This script runs in pages we do not own, alongside code we have never seen,
 * on browsers we did not choose. The worst thing it can do is not to lose a
 * page view; it is to throw. An exception here lands in the customer's console
 * as an error on their site, gets reported to them as a bug in their site, and
 * costs them an afternoon before anyone looks at the third-party script. Every
 * module underneath catches its own failures for the same reason; this is the
 * last net.
 */
import { findScript, readConfig } from './config.js';
import { createTracker } from './core.js';

/**
 * `window.panel`, with the queue shape.
 *
 * Calling it is the whole public API:
 *
 * ```js
 * panel('signup', { plan: 'pro' });   // a named event
 * panel.pageview('/cart');            // an explicit page view, for hash routers
 * ```
 *
 * ## The queue, and why it is read rather than required
 *
 * The snippet carries `defer`, so this file runs after the document has parsed
 * — which is right (it must not block the page) and means application code can
 * call `panel(…)` before it exists. A customer who needs that adds the two-line
 * stub every analytics vendor publishes:
 *
 * ```js
 * window.panel = window.panel || function () { (panel.q = panel.q || []).push(arguments); };
 * ```
 *
 * and we drain `q` on arrival. It is not in the pasted snippet, deliberately:
 * the install is one line, and a second line that most customers do not need is
 * a second thing to get wrong. It is in the documentation, for the ones who do.
 */
interface PanelGlobal {
  (name: unknown, props?: unknown): void;
  /** Calls made before this file loaded, from the optional stub. */
  q?: ArrayLike<unknown>[];
  pageview?: (path?: unknown) => void;
  event?: (name: unknown, props?: unknown) => void;
  /** Detaches the history wrappers. Exists for the browser suite; harmless in the wild. */
  stop?: () => void;
}

type PanelWindow = Window & { panel?: PanelGlobal; __panel?: boolean };

(function start(): void {
  try {
    const win = window as PanelWindow;

    /**
     * Refuse to be the second copy.
     *
     * Two tags on one page is not a hypothetical: it is what happens when the
     * snippet goes into a site template AND into a tag manager, which is the
     * single most common way an install ends up counting everything twice. The
     * second instance stops here rather than counting, and the first one
     * reports the collision through the script inventory in `neighbours.ts`, so
     * the customer is told instead of being left with doubled numbers.
     */
    if (win.__panel) return;
    win.__panel = true;

    const config = readConfig(findScript(win.document), win.location);
    if (!config) return;

    const tracker = createTracker(win, config);

    const existing = win.panel;

    const panel = function (name: unknown, props?: unknown): void {
      try {
        tracker.event(name, props);
      } catch {
        /* never into the host page */
      }
    } as PanelGlobal;

    panel.event = panel;
    panel.pageview = (path?: unknown): void => {
      try {
        tracker.pageview(path);
      } catch {
        /* never into the host page */
      }
    };
    panel.stop = (): void => {
      try {
        tracker.stop();
      } catch {
        /* never into the host page */
      }
    };

    win.panel = panel;

    // Drain whatever the stub collected, in order. Each call is guarded
    // individually: one malformed queued call must not swallow the rest.
    const queued = existing && existing.q;
    if (queued && queued.length) {
      for (let i = 0; i < queued.length; i++) {
        const args = queued[i];
        if (!args) continue;
        panel((args as ArrayLike<unknown>)[0], (args as ArrayLike<unknown>)[1]);
      }
    }
  } catch {
    /* See this file's header. There is no safe way to report this, and being
       silent is the contract. */
  }
})();
